import { createQueryKeys } from "@lukemorales/query-key-factory";
import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { useMutationWithError, usePaginateQuery } from "@/hooks";
import { adminApi } from "@/lib/api";
import { type Paginate, type SearchRequest } from "@/types";

import { type AdminReward } from "@/modules/recognition/admin/types";

export type RewardList = Paginate<AdminReward>;

export const rewards = createQueryKeys("admin-recognition-rewards", {
  all: (page?: number, filter?: SearchRequest) => [{ page, filter }],
  one: (id?: number) => [id],
});

const include = "attributes";

export const fetchRewardsQueryOptions = (
  page: number = 0,
  search?: SearchRequest,
) =>
  queryOptions({
    ...rewards.all(page, search),
    queryFn: () =>
      adminApi
        .get<RewardList>(`/recognition/rewards`, {
          params: { page, include, ...search },
        })
        .then((response) => response.data),
  });

export const useFetchRewards = (search?: SearchRequest) =>
  usePaginateQuery((page) => fetchRewardsQueryOptions(page, search));

export const fetchRewardQueryOptions = (id?: number) =>
  queryOptions({
    ...rewards.one(id),
    queryFn: () =>
      adminApi
        .get<AdminReward>(`/recognition/rewards/${id}`, {
          params: { include },
        })
        .then((response) => response.data),
    enabled: id !== undefined,
  });

export const useFetchReward = (id?: number) =>
  useQuery(fetchRewardQueryOptions(id));

export interface RewardRequest {
  name: string;
  description?: string | null;
  points: number;
  status: "active" | "inactive";
  image: string | null;
}

export const useCreateReward = () => {
  const queryClient = useQueryClient();
  return useMutationWithError({
    mutationFn: (data: RewardRequest) =>
      adminApi
        .post("/recognition/rewards/", data)
        .then((response) => response.data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: rewards.all._def,
      });
    },
  });
};

export const useUpdateReward = (id: number) => {
  const queryClient = useQueryClient();
  return useMutationWithError({
    mutationFn: (data: RewardRequest) =>
      adminApi
        .put(`/recognition/rewards/${id}`, data)
        .then((response) => response.data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: rewards.all._def,
      });
      queryClient.invalidateQueries(rewards.one(id));
    },
  });
};

export const useCreateOrUpdateReward = (id: number | undefined) => {
  const create = useCreateReward();
  const update = useUpdateReward(id!);
  return id ? update : create;
};

export const useDeleteReward = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => adminApi.delete(`recognition/rewards/${id}`),
    onSuccess: (_, id: number) => {
      queryClient.invalidateQueries({
        queryKey: rewards.all._def,
      });
      queryClient.invalidateQueries(rewards.one(id));
    },
  });
};
